// server-clock.js — client estimate of *server* wall time.
//
// Why a monotonic scale: Date.now() jumps when the user (or NTP) steps the
// clock. performance.now() does not. Stamps we put on records must not leap
// into the future because a laptop woke from sleep or someone toggled
// "set time automatically". Extrapolation is always
//   anchor.serverMs + (monoNow() − anchor.monoMs)
// Wall time is stored only to *detect* a jump; it never enters the estimate.
//
// Why we bias DOWN: a stamp in the future is a silent LWW poison (the server
// clamps it to now for comparison, then the real edit loses). A stamp a few
// hundred ms in the past still wins against older rows and can be bumped
// with prev+1. Lower bound of every sample: envelope `timestamp` /
// `record.updatedAt` as-is (minted before the response left), HTTP `Date`
// as-is (seconds, truncated — true time is ≥ it). Never add RTT/2 or 500 ms.
//
// Why X-Client-Time stays a raw Date.now(): the step-1 observer keys on the
// client's real wall. Feeding it serverNow() would double-correct and go
// blind. Collectors must not import this module for that header.

const RTT_MAX_MS = 5000;
const JUMP_MS = 1000;
const ANCHOR_MAX_AGE_MS = 5 * 60 * 1000;
const BACKWARD_REJECT_MS = 5000;
const RESYNC_MIN_INTERVAL_MS = 5000;

/** @typedef {{ serverMs: number, monoMs: number, wallMs: number, uncertaintyMs: number, sampledAtMono: number, stale: boolean }} ClockAnchor */

/** @type {ClockAnchor | null} */
let anchor = null;
/** @type {null | (() => number)} */
let monoNowOverride = null;
let listenersAttached = false;
let resyncInFlight = false;
let lastResyncMono = Number.NEGATIVE_INFINITY;
/** @type {AbortController | null} */
let resyncAbort = null;
/** Bumped on reset so in-flight probes cannot plant after teardown. */
let sampleEpoch = 0;
/** Bumped per started probe so an older done() cannot clear a newer flight. */
let resyncGeneration = 0;

const isVitest = Boolean(import.meta.env && import.meta.env.VITEST);
/** Vitest: no network probe unless a clock test opts in. Prevents a 5s hang to :3001. */
let allowNetworkResync = !isVitest;

function currentMono() {
  if (typeof monoNowOverride === 'function') return monoNowOverride();
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function headerGet(headers, name) {
  if (!headers) return null;
  if (typeof headers.get === 'function') {
    try { return headers.get(name); } catch { return null; }
  }
  return headers[name] ?? headers[name.toLowerCase()] ?? null;
}

/** Parse a clock field. null/undefined/''/whitespace/NaN are missing — never epoch 0. */
function coerceClockMs(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    if (s === '') return null;
    const n = Number(s);
    if (Number.isFinite(n)) return n;
    const parsed = Date.parse(s);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/** True when json is an envelope from our API (not a captive portal / proxy page).
 * A single marker (`status` / `data` / `ok` / `timestamp`) is not enough — captive
 * portals and proxies often echo one of those. Require a pair of known fields. */
export function isOurClockEnvelope(json) {
  if (json == null || typeof json !== 'object' || Array.isArray(json)) return false;
  let n = 0;
  if ('status' in json) n++;
  if (typeof json.ok === 'boolean') n++;
  if (json.timestamp != null) n++;
  if (json.version != null) n++;
  if ('data' in json) n++;
  if (json.record != null && typeof json.record === 'object') n++;
  return n >= 2;
}

function estimateNow() {
  if (!anchor) return Date.now();
  return Math.round(anchor.serverMs + (currentMono() - anchor.monoMs));
}

function attachListeners() {
  if (listenersAttached) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibility);
  listenersAttached = true;
}

function detachListeners() {
  if (!listenersAttached) return;
  if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
  listenersAttached = false;
}

function checkClockJump() {
  if (!anchor) return;
  const wallElapsed = Date.now() - anchor.wallMs;
  const monoElapsed = currentMono() - anchor.monoMs;
  if (Math.abs(wallElapsed - monoElapsed) > JUMP_MS) markStaleAndResync();
}

function onOnline() {
  checkClockJump();
  requestResync();
}

function onVisibility() {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    checkClockJump();
    requestResync();
  }
}

function shouldReplace(uncertaintyMs) {
  if (!anchor) return true;
  if (anchor.stale) return true;
  if (uncertaintyMs <= anchor.uncertaintyMs) return true;
  if (currentMono() - anchor.sampledAtMono > ANCHOR_MAX_AGE_MS) return true;
  return false;
}

function markStaleAndResync() {
  const becomingStale = Boolean(anchor && !anchor.stale);
  if (anchor) anchor.stale = true;
  requestResync({ bypassInterval: becomingStale });
}

/** @param {object} sample @returns {boolean} */
export function noteServerTimeSample(sample) {
  const serverMs = sample ? coerceClockMs(sample.serverMs) : null;
  if (serverMs == null) return false;
  const receivedMono = Number.isFinite(sample.receivedMono) ? sample.receivedMono : currentMono();
  const receivedWall = Number.isFinite(sample.receivedWall) ? sample.receivedWall : Date.now();
  const rttMs = Number.isFinite(sample.rttMs)
    ? Number(sample.rttMs)
    : (Number.isFinite(sample.sentMono) ? receivedMono - sample.sentMono : 0);
  const sentMono = Number.isFinite(sample.sentMono) ? sample.sentMono : receivedMono - rttMs;
  const sentWall = Number.isFinite(sample.sentWall) ? sample.sentWall : receivedWall - rttMs;
  const resolutionMs = Number.isFinite(sample.resolutionMs) ? Number(sample.resolutionMs) : 0;

  if (sample.headers) {
    const age = headerGet(sample.headers, 'Age');
    if (age != null && String(age) !== '') return false;
    if (sample.expectDate && !headerGet(sample.headers, 'Date')) return false;
  }

  if (rttMs > RTT_MAX_MS) return false;
  if (Math.abs((receivedWall - sentWall) - (receivedMono - sentMono)) > JUMP_MS) return false;

  // True server time does not walk backwards. A sample that drops the live
  // estimate by more than 5s is a stale/cached value — keep the current
  // anchor unless it is already stale or older than ANCHOR_MAX_AGE_MS.
  if (
    anchor
    && !anchor.stale
    && (currentMono() - anchor.sampledAtMono) <= ANCHOR_MAX_AGE_MS
    && (estimateNow() - serverMs) > BACKWARD_REJECT_MS
  ) {
    return false;
  }

  const uncertaintyMs = rttMs + resolutionMs;
  if (!shouldReplace(uncertaintyMs)) return false;

  anchor = {
    serverMs,
    monoMs: receivedMono,
    wallMs: receivedWall,
    uncertaintyMs,
    sampledAtMono: receivedMono,
    stale: false,
  };
  return true;
}

/** Heartbeat / WS lower bound: may only raise the estimate. Never lowers it. */
export function noteServerLowerBound(serverMs) {
  const n = Number(serverMs);
  if (!Number.isFinite(n)) return;
  if (n <= estimateNow()) return;
  const m = currentMono();
  const w = Date.now();
  // Date-header anchors are truncated to 1s. A WS heartbeat a few hundred ms
  // above the estimate is that truncation, not clock jump — raise in place
  // and do not probe /api/health (prod: 5–6× health storm after v32.26.43).
  const raiseMs = n - estimateNow();
  if (anchor && !anchor.stale && raiseMs <= anchor.uncertaintyMs) {
    anchor.serverMs = n;
    anchor.monoMs = m;
    anchor.wallMs = w;
    return;
  }
  anchor = {
    serverMs: n,
    monoMs: m,
    wallMs: w,
    uncertaintyMs: anchor ? anchor.uncertaintyMs : 1000,
    sampledAtMono: m,
    stale: true,
  };
  requestResync();
}

export function serverNow() {
  if (!anchor) return Date.now();
  checkClockJump();
  return estimateNow();
}

export function clockState() {
  if (!anchor) return { synced: false, uncertaintyMs: null, stale: false, offsetMs: 0 };
  return {
    synced: true,
    uncertaintyMs: anchor.uncertaintyMs,
    stale: Boolean(anchor.stale),
    offsetMs: Math.round(estimateNow() - Date.now()),
  };
}

export function beginClockSample() {
  return { sentMono: currentMono(), sentWall: Date.now(), epoch: sampleEpoch };
}

export function endClockSample(started) {
  const receivedMono = currentMono();
  const receivedWall = Date.now();
  return {
    sentMono: started.sentMono,
    sentWall: started.sentWall,
    receivedMono,
    receivedWall,
    rttMs: receivedMono - started.sentMono,
    epoch: started && started.epoch,
  };
}

/** @param {boolean} allow */
export function _setAllowNetworkResync(allow) {
  allowNetworkResync = Boolean(allow);
}

/**
 * Downward-biased sample from an HTTP response.
 * Anchor sources (in order): envelope `timestamp` → HTTP `Date`.
 * `record.updatedAt` is a lower bound only — it is a row stamp, not "server
 * now", and an idempotent action may echo a stale value.
 * Missing/empty/null fields → skip that source; if none remain, do not plant.
 */
export function noteHttpResponseSample(res, json, timing, opts = {}) {
  if (!timing) return false;
  if (timing.epoch !== undefined && timing.epoch !== sampleEpoch) return false;
  const headers = res && res.headers;
  if (headerGet(headers, 'Age') != null && String(headerGet(headers, 'Age')) !== '') {
    return false;
  }

  const ours = isOurClockEnvelope(json);
  const body = json && typeof json === 'object' && !Array.isArray(json) ? json : null;
  const ts = ours && body ? coerceClockMs(body.timestamp) : null;
  const updatedAt = ours && body && body.record && typeof body.record === 'object'
    ? coerceClockMs(body.record.updatedAt)
    : null;

  let serverMs = null;
  let resolutionMs = 0;
  const ok = !!(res && res.ok);
  // 2xx + our envelope only. A 502 with our JSON (or a captive 200 HTML
  // whose json() failed) must not move the anchor — Date without our body
  // is never a sample.
  if (ok && ts != null) {
    serverMs = ts;
  } else if (ok && ours) {
    const dateH = headerGet(headers, 'Date');
    if (dateH) {
      const parsed = Date.parse(dateH);
      if (Number.isFinite(parsed)) {
        serverMs = parsed;
        resolutionMs = 1000;
      }
    }
  }

  let planted = false;
  if (serverMs != null) {
    planted = noteServerTimeSample({
      serverMs,
      rttMs: timing.rttMs,
      resolutionMs,
      sentMono: timing.sentMono,
      receivedMono: timing.receivedMono,
      sentWall: timing.sentWall,
      receivedWall: timing.receivedWall,
      headers,
      expectDate: Boolean(opts.expectDate) && resolutionMs === 1000,
    });
  }
  if (ok && updatedAt != null) {
    noteServerLowerBound(updatedAt);
  }
  return planted;
}

function healthUrl() {
  // Do not import endpoints.js — it reads window.location at load and throws
  // in the backend (node) vitest project.
  if (typeof window === 'undefined' || !window.location) return '/api/health';
  const h = window.location.hostname;
  const base = (h === 'localhost' || h === '127.0.0.1') ? 'http://localhost:3001/api' : '/api';
  return `${base}/health`;
}

export function requestResync(opts = {}) {
  if (!allowNetworkResync) return;
  attachListeners();
  const m = currentMono();
  if (resyncInFlight && !opts.bypassInterval) return;
  if (!opts.bypassInterval && m - lastResyncMono < RESYNC_MIN_INTERVAL_MS) return;
  lastResyncMono = m;
  const generation = ++resyncGeneration;
  resyncInFlight = true;
  if (resyncAbort) {
    try { resyncAbort.abort(); } catch { /* already aborted */ }
  }
  const ac = new AbortController();
  resyncAbort = ac;
  const timer = setTimeout(() => { try { ac.abort(); } catch { /* ignore */ } }, 5000);
  const started = beginClockSample();
  const doFetch = typeof fetch === 'function' ? fetch : null;
  const done = () => {
    clearTimeout(timer);
    if (resyncAbort === ac) resyncAbort = null;
    if (generation === resyncGeneration) resyncInFlight = false;
  };
  if (!doFetch) { done(); return; }
  let pending;
  try {
    pending = doFetch(healthUrl(), { cache: 'no-store', signal: ac.signal });
  } catch {
    done();
    return;
  }
  Promise.resolve(pending)
    .then((res) => {
      if (ac.signal.aborted) return;
      const timing = endClockSample(started);
      return Promise.resolve()
        .then(() => (res && typeof res.json === 'function' ? res.json() : null))
        .catch(() => null)
        .then((json) => {
          if (ac.signal.aborted) return;
          noteHttpResponseSample(res, json, timing, { expectDate: true });
        });
    })
    .catch(() => { /* probe is best-effort */ })
    .finally(done);
}

export function _resetServerClock() {
  sampleEpoch += 1;
  resyncGeneration += 1;
  allowNetworkResync = !isVitest;
  anchor = null;
  monoNowOverride = null;
  lastResyncMono = Number.NEGATIVE_INFINITY;
  resyncInFlight = false;
  if (resyncAbort) {
    try { resyncAbort.abort(); } catch { /* ignore */ }
    resyncAbort = null;
  }
  detachListeners();
}

export function _setServerClockMonoNow(fn) {
  monoNowOverride = typeof fn === 'function' ? fn : null;
}

// One probe at module load in the real browser. Vitest must not hit a local
// :3001 (that would plant a live Date sample into every suite).
if (typeof window !== 'undefined' && !isVitest) {
  requestResync();
}
